import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { EMPTY, catchError, map } from 'rxjs';

import { TranslatePipe } from '@ngx-translate/core';
import { CountryISO, NgxIntlTelInputModule, PhoneNumberFormat, SearchCountryField } from '@moddi3/ngx-intl-tel-input';

import { EFormInputType } from '@common/enums';
import * as typeGuards from '@common/type-guards/form-elements.type-guards';
import { CountryISOService } from '@common/services/REST/country-iso.http.service';
import { RemovePTags } from '@common/pipes/remove-p-tag.pipe';
import { SpIntlTelInputA11yDirective } from '@common/directives/intl-tel-input-a11y.directive';

import type { LoginEduFormViewData } from '@sites/entity-core/src/widget/login-edu/styles/login-edu.css-vars';

/** Denormalised tariff snapshot the widget persists alongside the course. */
export type CourseTariffPreview = {
    isPaid: boolean;
    tariffName: string;
    price: number;
    currency: string;
};

/**
 * Read-only preview of the registration form as the visitor will see it. Unlike
 * the sign-in form, this one renders a field per element type and resolves the
 * visitor's country for the phone input, so it earns its own component.
 */
@Component({
    selector: 'web-register-form-view',
    templateUrl: './register-form-view.component.html',
    styleUrls: ['./register-form-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, NgxIntlTelInputModule, SpIntlTelInputA11yDirective, TranslatePipe, RemovePTags],
})
export class RegisterFormViewComponent {
    private readonly countryISOService = inject(CountryISOService);

    public readonly data = input.required<LoginEduFormViewData>();
    public readonly showAuthorizationFormLink = input<boolean>(false);
    public readonly courseTariffDetails = input<CourseTariffPreview | null>(null);
    public readonly course = input<{ name?: string } | null>(null);

    public readonly typeGuards = typeGuards;
    public readonly formInputType = EFormInputType;
    protected readonly SearchCountryField = SearchCountryField;
    protected readonly PhoneNumberFormat = PhoneNumberFormat;

    /**
     * Country preselected in the phone input, resolved from the visitor's IP.
     * `toSignal` unsubscribes on destroy — the previous hand-rolled subscription
     * had no teardown and called `detectChanges()` from its callback, which
     * throws if the response lands after the panel is closed. A failing geoip
     * lookup must not break the preview either, hence the fallback to the
     * initial value.
     */
    public readonly countryISO = toSignal(
        this.countryISOService.getCountryISO().pipe(
            map((response: { country_code: string }) => response.country_code.toLowerCase() as CountryISO),
            catchError(() => EMPTY)
        ),
        { initialValue: CountryISO.Ukraine }
    );
}
